import React, { useState } from 'react';

interface MFAVerificationProps {
  qrCode: string;
  email: string;
  onSubmit: (token: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const MFAVerificationPopup: React.FC<MFAVerificationProps> = ({
  qrCode,
  email,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [token, setToken] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  const isSubmitting = isLoading || localLoading;
  const hasQRCode = qrCode && qrCode.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLocalLoading(true);
    try {
      if (!token.trim()) throw new Error('Verification token is required');
      await onSubmit(token);
    } catch (err) {
      console.log(err);
      setError('Invalid Token');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleResetQR = async () => {
    setResetError('');
    setResetLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}qix-deploy/api/admin/api/mfa/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.status === 200) {
        setResetSuccess(true);
        setTimeout(() => {
          setResetSuccess(false);
          setShowResetConfirm(false);
        }, 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reset QR code');
      }
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to reset QR code');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetCancel = () => {
    setShowResetConfirm(false);
    setResetError('');
    setResetSuccess(false);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
      <div
        className="relative w-[400px] max-w-[95vw] max-h-[90vh] overflow-y-auto rounded-[14px] p-8"
        style={{
          background: '#111116',
          border: '1px solid #32323d',
          boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
        }}
      >
        {/* Logo mark */}
        <div className="flex justify-center mb-5">
          <div
            className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white font-black text-[14px]"
            style={{
              background: 'linear-gradient(135deg,#6366f1,#a855f7)',
              fontFamily: "'Syne',sans-serif",
            }}
          >
            Qx
          </div>
        </div>

        {!showResetConfirm ? (
          <>
            <h2
              className="text-[20px] font-bold text-center mb-1"
              style={{ fontFamily: "'Syne',sans-serif", color: '#eeeef5' }}
            >
              Multi-Factor Authentication
            </h2>
            <p className="text-center text-[13px] mb-6" style={{ color: '#8e8ea8' }}>
              {hasQRCode
                ? 'Scan the QR code with Google Authenticator and enter the verification code'
                : 'Enter verification code from your authenticator app'}
            </p>

            {/* Error */}
            {error && (
              <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-[6px] mb-4 text-[12.5px]"
                style={{
                  background: 'rgba(244,63,94,0.1)',
                  border: '1px solid rgba(244,63,94,0.25)',
                  color: '#f43f5e',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/>
                </svg>
                {error}
              </div>
            )}

            {/* QR Code */}
            {hasQRCode && (
              <div className="mb-6 flex flex-col items-center gap-3">
                <div
                  className="p-3 rounded-[8px]"
                  style={{ background: '#fff', border: '1px solid #26262f' }}
                >
                  <img
                    src={`data:image/png;base64,${qrCode}`}
                    alt="QR Code for Google Authenticator"
                    className="w-44 h-44 object-contain"
                  />
                </div>
                <div className="text-center" style={{ color: '#8e8ea8', fontSize: '11.5px' }}>
                  <p className="font-semibold mb-1" style={{ color: '#eeeef5' }}>Setup Instructions:</p>
                  <p>1. Open Google Authenticator app</p>
                  <p>2. Tap the "+" button to add an account</p>
                  <p>3. Scan this QR code</p>
                  <p>4. Enter the 6-digit code below</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label
                  className="block text-[12px] font-medium mb-1.5"
                  style={{ color: '#8e8ea8' }}
                >
                  Verification Code
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full rounded-[6px] px-3 py-2.5 text-[20px] text-center font-mono tracking-[0.35em] outline-none transition-all"
                  style={{
                    background: '#17171e',
                    border: `1px solid ${token.length === 6 ? '#6366f1' : '#26262f'}`,
                    color: '#eeeef5',
                    boxShadow: token.length === 6 ? '0 0 0 3px rgba(99,102,241,0.14)' : 'none',
                  }}
                  placeholder="000000"
                  maxLength={6}
                  disabled={isSubmitting}
                  autoComplete="off"
                  autoFocus
                />
                <p className="text-center mt-1" style={{ fontSize: '11px', color: '#52525f' }}>
                  Enter the 6-digit code from Google Authenticator
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-[6px] text-[13px] font-medium transition-all"
                  style={{
                    background: '#17171e',
                    border: '1px solid #32323d',
                    color: '#eeeef5',
                    opacity: isSubmitting ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || token.length !== 6}
                  className="px-4 py-2 rounded-[6px] text-[13px] font-medium transition-all flex items-center gap-2"
                  style={{
                    background: isSubmitting || token.length !== 6 ? '#3d3f6b' : '#6366f1',
                    color: '#fff',
                    opacity: isSubmitting || token.length !== 6 ? 0.6 : 1,
                    cursor: isSubmitting || token.length !== 6 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSubmitting && (
                    <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                  )}
                  {isSubmitting ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowResetConfirm(true)}
                disabled={isSubmitting}
                className="text-[12px] underline transition-colors"
                style={{ color: '#818cf8' }}
              >
                Lost access? Reset QR Code
              </button>
            </div>
          </>
        ) : (
          <>
            <h2
              className="text-[20px] font-bold text-center mb-1"
              style={{ fontFamily: "'Syne',sans-serif", color: '#eeeef5' }}
            >
              Reset QR Code
            </h2>
            <p className="text-center text-[13px] mb-6" style={{ color: '#8e8ea8' }}>
              Send a new QR code to{' '}
              <span className="font-semibold" style={{ color: '#eeeef5' }}>{email}</span>?
            </p>

            {resetError && (
              <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-[6px] mb-3 text-[12.5px]"
                style={{
                  background: 'rgba(244,63,94,0.1)',
                  border: '1px solid rgba(244,63,94,0.25)',
                  color: '#f43f5e',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/>
                </svg>
                {resetError}
              </div>
            )}

            {resetSuccess && (
              <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-[6px] mb-3 text-[12.5px]"
                style={{
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.25)',
                  color: '#22c55e',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 6.5l4 4 5-5"/>
                </svg>
                Reset email sent! Check your email for the new QR code.
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={handleResetCancel}
                disabled={resetLoading}
                className="px-4 py-2 rounded-[6px] text-[13px] font-medium transition-all"
                style={{
                  background: '#17171e',
                  border: '1px solid #32323d',
                  color: '#eeeef5',
                  opacity: resetLoading ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetQR}
                disabled={resetLoading}
                className="px-4 py-2 rounded-[6px] text-[13px] font-medium flex items-center gap-2 transition-all"
                style={{
                  background: resetLoading ? '#3d3f6b' : '#6366f1',
                  color: '#fff',
                  opacity: resetLoading ? 0.6 : 1,
                }}
              >
                {resetLoading && (
                  <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                )}
                {resetLoading ? 'Sending…' : 'Send Reset Email'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MFAVerificationPopup;